import React, { useEffect, useState } from 'react';
import {
  Button,
  makeStyles,
  Step,
  StepContent,
  StepLabel,
  Stepper,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import {
  MeasurementCardResponse,
  PositionResponse,
  ValidationTypeEnumResponse,
} from '../../.generated/admin-api';
import { RegistrationFlowSection } from './RegistrationFlowSection';
import { FieldType, FieldValueState } from '.';
import { withLocation, WithLocationProps } from './WithLocation';
import { ApplicationState } from '../../store';
import { useDispatch, useSelector } from 'react-redux';
import { notificationActions } from '../../store/notification/actions';
import { Message } from '../../store/notification/types';
import { apiTranslate } from '../../translations/translations';
import { validateField } from './fieldValidation';
import { stackActions } from '../../store/stack/actions';

interface RegistrationFlowInputProps extends WithLocationProps {
  measurementCard: MeasurementCardResponse;
  onFlowCompleted?: () => void;
  onAutofillChanged?: (autoFillFields: FieldValueState[]) => void;
  disabled?: boolean;
}

const useStyles = makeStyles(() => ({
  backButton: {
    paddingLeft: 0,
  },
}));

// These field ids are currently hardcoded
export const locationFieldId = 'a2572589db244d2ab361de4f8068c4b3';
export const dateFieldId = '2e6f1568051e4115a028cdc51fed47a3';
export const stackNumberFieldId = '4eebd0579703487098ef127c34de43ea';

const RegistrationFlow: React.FC<RegistrationFlowInputProps> = (props) => {
  const { t } = useTranslation(['translations']);

  const dispatch = useDispatch();

  const classes = useStyles();

  const {
    measurementCard,
    watchLocation,
    onFlowCompleted,
    onAutofillChanged,
    disabled,
  } = props;

  const departmentId = useSelector(
    (state: ApplicationState) => state.user.selectedDepartment?.id,
  );

  const fieldValues = useSelector((state: ApplicationState) => {
    const deptId = state.user.selectedDepartment?.id;
    if (!deptId) {
      return [];
    }
    const values = state.stacks.departments?.[deptId]?.fieldValues;
    if (!values) {
      return [];
    }
    return values;
  });

  if (!departmentId) {
    // TODO: DHM: How to handle this failure?
    // Should we restart the action picking the default department, when it's implemented properly?
    return <></>;
  }

  const [locationField, setLocationField] = useState<FieldType>();
  const [activeStep, setActiveStep] = useState(0);
  //   const [fieldValues, setFieldValues] = useState<FieldValueState[]>([]);
  const [isAllValuesValid, setIsAllValuesValid] = useState<boolean>(false);
  const [
    isAllRequiredFieldsValid,
    setIsAllRequiredFieldsValid,
  ] = useState<boolean>(false);
  const [isSectionValid, setIsSectionValid] = useState<boolean>(false);
  const [location, setLocation] = useState<GeolocationPosition>();

  const sections = measurementCard.displayConfigurationPreregistration.sections;
  const maxSteps = sections.length;

  const section = sections[activeStep];

  const isLastStep = activeStep === maxSteps - 1;
  const nextButtonText = isLastStep ? t('common.create') : t('common.next');

  const handleNewLocation = (position: GeolocationPosition) => {
    setLocation(position);
  };

  const fieldValueChange = (
    fieldId: string,
    value: unknown,
    isValid: boolean,
  ): void => {
    dispatch(
      stackActions.updateFieldValue(departmentId, { fieldId, value, isValid }),
    );
  };

  const updateFieldValue = (field: FieldType, value: unknown): void => {
    const validationResult = validateField(field, value);
    fieldValueChange(field.id, value, validationResult.isValid);
  };

  useEffect(() => {
    const locationField = measurementCard.fields.find(
      (field) => field.id === locationFieldId,
    );

    setLocationField(locationField);

    const dateField = measurementCard.fields.find(
      (field) => field.id === dateFieldId,
    );

    if (dateField) {
      const dateValue = new Date().toISOString();
      updateFieldValue(dateField, dateValue);
    }
  }, [measurementCard]);

  useEffect(() => {
    if (locationField) {
      let positionValue: PositionResponse | undefined;

      if (location) {
        positionValue = {
          latitude: location.coords.latitude,
          longitude: location.coords.longitude,
          height: location.coords.altitude ?? 0, // TODO: DHM: Altitude not always available
          accuracy: location.coords.accuracy,
        };
      }

      updateFieldValue(locationField, positionValue);
    }
  }, [location, locationField]);

  useEffect(() => {
    if (disabled !== true) {
      return watchLocation(handleNewLocation);
    }
  }, []);

  const handleNext = () => {
    if (isLastStep) {
      if (!isAllValuesValid) {
        dispatch(
          notificationActions.addMessage(
            Message.error(t('Some fields are not valid')),
          ),
        );
        return;
      }

      if (!isAllRequiredFieldsValid) {
        dispatch(
          notificationActions.addMessage(
            Message.error(t('Some internal fields is not valid, contact HD')),
          ),
        );
        return;
      }

      const stackNumberField = fieldValues.find(
        (field) => field.fieldId === stackNumberFieldId,
      );
      const dateField = fieldValues.find(
        (field) => field.fieldId === dateFieldId,
      );

      dispatch(
        stackActions.createStack(
          departmentId,
          measurementCard.id,
          fieldValues,
          stackNumberField ? (stackNumberField.value as string) : '',
          dateField ? (dateField.value as string) : '',
        ),
      );

      if (onFlowCompleted) {
        onFlowCompleted();
      }

      return;
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const ensureAllFieldsIsValid = (fieldIds: string[]): boolean => {
    return fieldIds.every((fieldId) => {
      const fields = fieldValues.filter(
        (fieldValue) => fieldValue.fieldId === fieldId,
      );

      if (fields.length === 0) {
        return false;
      }

      return fields.every((f) => f.isValid);
    });
  };

  useEffect(() => {
    const isAllValuesValid = fieldValues.every((field) => field.isValid);

    setIsAllValuesValid(isAllValuesValid);

    const requiredFields = measurementCard.fields.filter((field) =>
      field.validationRules?.some(
        (rule) =>
          rule.type === ValidationTypeEnumResponse.REQUIRED_FOR_PREREGISTRATION,
      ),
    );

    const isAllRequiredFieldsValid = ensureAllFieldsIsValid(
      requiredFields.map((field) => field.id),
    );

    setIsAllRequiredFieldsValid(isAllRequiredFieldsValid);

    const isFieldsinSectionValid = ensureAllFieldsIsValid(section.fields);

    setIsSectionValid(isFieldsinSectionValid);
  }, [fieldValues, activeStep]);

  return (
    <>
      <Stepper activeStep={activeStep} orientation="vertical">
        {sections.map((section, index) => (
          <Step key={index}>
            <StepLabel>{apiTranslate(section.title)}</StepLabel>
            <StepContent>
              <RegistrationFlowSection
                measurementCard={measurementCard}
                onAutofillChanged={onAutofillChanged}
                section={section}
                fieldValues={fieldValues}
                disabled={disabled}
                backButton={
                  <Button
                    onClick={handleBack}
                    disabled={activeStep === 0}
                    className={classes.backButton}
                  >
                    {t('common.back')}
                  </Button>
                }
                nextButton={
                  isLastStep && disabled === true ? null : (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleNext}
                      disabled={
                        isLastStep
                          ? !isAllValuesValid || !isAllRequiredFieldsValid
                          : !isSectionValid
                      }
                    >
                      {nextButtonText}
                    </Button>
                  )
                }
                onFieldValueChangeOrValidated={fieldValueChange}
              />
            </StepContent>
          </Step>
        ))}
      </Stepper>
    </>
  );
};

export default withLocation(RegistrationFlow);
